import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';
import AviveStatuses from '../JurisdictionView/AviveAED/AviveStatuses';
import { formatPhone } from '../JurisdictionView/AviveAED/PhoneField';
import { normalizePhone } from '../JurisdictionView/AviveAED/aviveSlice';
import MapCard from '../Map/MapCard';
import { SpatialLayerCardContainer } from './MapCard.styles';

/**
 * Resolve the live spatial object for the open map card.
 *
 * UPDATE_DYNAMIC_LAYER replaces only the matching object identity, so:
 * - updates to the open object → new ref → useSelector re-renders the card
 * - updates to any other object → same ref → useSelector skips the re-render
 */
export const selectLiveSpatialObject = state => {
  const mapCardData = state.mapCard?.mapCardData;
  const layerKey = mapCardData?.layerData?.key;
  const externalId = mapCardData?.spatialObject?.external_id;
  const snapshot = mapCardData?.spatialObject;

  if (!layerKey || !externalId) {
    return snapshot;
  }

  const spatialLayer = state.spatialLayers?.dynamicLayers?.find(
    layer => layer.key === layerKey,
  );
  return (
    spatialLayer?.spatial_objects?.find(
      object => object.external_id === externalId,
    ) ?? snapshot
  );
};

const DynamicLayerCard = () => {
  const mapCardData = useSelector(state => state.mapCard.mapCardData);
  // Re-renders when the open object identity changes (live status/ETA/address).
  const spatialObject = useSelector(selectLiveSpatialObject);

  /**
   * Dynamic layers have the `display_schema` as the `attributes`.
   */
  const getDisplaySchema = useCallback(() => {
    return mapCardData?.layerData?.attributes;
  }, [mapCardData]);

  const isAviveRequest = mapCardData?.layerData?.key === 'avive_aed_request';

  const renderAviveStatuses = () => {
    if (!isAviveRequest) {
      return null;
    }

    const phone = spatialObject?.attributes?.phone_number;
    return <AviveStatuses phone={phone} pb={16} />;
  };

  let data = spatialObject?.attributes;
  if (isAviveRequest) {
    data = {
      ...data,
      phone_number: formatPhone(normalizePhone(data?.phone_number), {
        onlyDashes: true,
      }),
    };
  }

  return (
    <SpatialLayerCardContainer>
      <MapCard
        data={data}
        displaySchema={getDisplaySchema()}
        objectData={spatialObject}
        createdTime={spatialObject?.date_created}
        layer={mapCardData?.layerData}
        title={mapCardData?.layerData.title}
        renderCustom={isAviveRequest ? renderAviveStatuses : () => null}
        isAviveRequest={isAviveRequest}
      />
    </SpatialLayerCardContainer>
  );
};

export default DynamicLayerCard;
